import { useMutation } from '@apollo/client/react';
import { useCallback, useState } from 'react';
import type {
  ChartCardExportFormat,
  ChartCardSeries,
  ChartKind,
  ChartValueFormat
} from '@insio/ui-toolkit';
import { ExportChartDocument, type ChartExportFormat, type ExportChartInput } from '@/graphql';
import { downloadBase64File } from '@/utils/file';

const FORMAT_MAP: Record<ChartCardExportFormat, ChartExportFormat> = {
  pdf: 'PDF',
  xlsx: 'XLSX'
};

export interface ChartExportRequest {
  format: ChartCardExportFormat;
  title?: string;
  kind: ChartKind;
  yFormat?: ChartValueFormat;
  /** Display-ready series — labels already localized. */
  series: ChartCardSeries[];
  /** Lazily captures the rendered chart as base64 PNG — needed for PDF only. */
  capturePng: () => Promise<string>;
  /** Localized header for the x-value column in Excel. */
  labelHeader?: string;
}

export interface UseChartExportReturn {
  /** Currently exporting format, null when idle */
  exporting: ChartCardExportFormat | null;
  hasError: boolean;
  exportChart: (request: ChartExportRequest) => Promise<boolean>;
}

/** Exports a rendered chart to PDF/XLSX via the backend and downloads the file. */
export function useChartExport(): UseChartExportReturn {
  const [exporting, setExporting] = useState<ChartCardExportFormat | null>(null);
  const [hasError, setHasError] = useState(false);
  const [runExport] = useMutation(ExportChartDocument);

  const exportChart = useCallback(
    async (request: ChartExportRequest): Promise<boolean> => {
      setExporting(request.format);
      setHasError(false);

      try {
        const input: ExportChartInput = {
          format: FORMAT_MAP[request.format],
          title: request.title,
          kind: request.kind,
          yFormat: request.yFormat,
          labelHeader: request.labelHeader,
          series: request.series.map(s => ({
            label: s.label,
            currency: s.currency,
            points: s.points.map(point => ({ x: point.x, y: point.y }))
          })),
          imageBase64: request.format === 'pdf' ? await request.capturePng() : undefined
        };

        const { data } = await runExport({ variables: { input } });
        const result = data?.exportChart;
        if (!result) {
          setHasError(true);
          return false;
        }

        downloadBase64File(result.file, result.filename, result.mimeType);
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Chart export failed:', err);
        setHasError(true);
        return false;
      } finally {
        setExporting(null);
      }
    },
    [runExport]
  );

  return { exporting, hasError, exportChart };
}
